import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface BetaUserConfig {
  id: string;
  user_id: string;
  beta_tier: 'free' | 'premium' | 'unlimited';
  ai_credits_remaining: number;
  daily_limit: number;
  monthly_budget_cents: number;
  monthly_spend_cents: number;
  last_reset_date: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DailyUsage {
  id: string;
  user_id: string;
  usage_date: string;
  operations_count: number;
  total_cost_cents: number;
  operations_by_type: Record<string, number>;
  created_at: string;
  updated_at: string;
}

export const useBetaUserConfig = () => {
  const { user } = useAuth();
  const [config, setConfig] = useState<BetaUserConfig | null>(null);
  const [todayUsage, setTodayUsage] = useState<DailyUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch beta user config
      const { data: configData, error: configError } = await supabase
        .from('beta_user_config')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (configError && configError.code !== 'PGRST116') {
        throw configError;
      }

      // If no config exists, create one
      if (!configData) {
        const { data: newConfig, error: createError } = await supabase
          .from('beta_user_config')
          .insert({
            user_id: user.id,
            beta_tier: 'free',
            ai_credits_remaining: 100,
            daily_limit: 10,
            monthly_budget_cents: 500
          })
          .select()
          .single();

        if (createError) throw createError;
        setConfig(newConfig as BetaUserConfig);
      } else {
        setConfig(configData as BetaUserConfig);
      }

      // Fetch today's usage
      const today = new Date().toISOString().split('T')[0];
      const { data: usageData, error: usageError } = await supabase
        .from('daily_ai_usage')
        .select('*')
        .eq('user_id', user.id)
        .eq('usage_date', today)
        .single();

      if (usageError && usageError.code !== 'PGRST116') {
        console.warn('Error fetching daily usage:', usageError);
      } else if (usageData) {
        setTodayUsage({
          ...usageData,
          operations_by_type: (usageData.operations_by_type as any) || {}
        } as DailyUsage);
      }
    } catch (err: any) {
      console.error('Error fetching beta config:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkCanPerformOperation = async (operationType: string = 'content_analysis'): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.rpc('can_user_perform_ai_operation', {
        _user_id: user.id,
        _operation_type: operationType
      });

      if (error) {
        console.error('Error checking operation permission:', error);
        return false;
      }

      return data;
    } catch (err) {
      console.error('Error checking operation permission:', err);
      return false;
    }
  };

  const recordUsage = async (
    operationType: string,
    platform: string,
    costCents: number = 0,
    tokensUsed: number = 0,
    modelUsed?: string,
    requestData?: any,
    responseData?: any
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.rpc('record_ai_usage', {
        _user_id: user.id,
        _operation_type: operationType,
        _platform: platform,
        _cost_cents: costCents,
        _tokens_used: tokensUsed,
        _model_used: modelUsed,
        _request_data: requestData,
        _response_data: responseData
      });

      if (error) {
        console.error('Error recording usage:', error);
        return false;
      }

      // Refresh config and usage after recording
      await fetchConfig();
      return data;
    } catch (err) {
      console.error('Error recording usage:', err);
      return false;
    }
  };

  const updateTier = async (newTier: 'free' | 'premium' | 'unlimited') => {
    if (!user || !config) return;

    try {
      const updates: Partial<BetaUserConfig> = {
        beta_tier: newTier,
        ai_credits_remaining: newTier === 'free' ? 100 : newTier === 'premium' ? 500 : 999999,
        daily_limit: newTier === 'free' ? 10 : newTier === 'premium' ? 50 : 999999,
        monthly_budget_cents: newTier === 'free' ? 500 : newTier === 'premium' ? 2000 : 999999
      };

      const { data, error } = await supabase
        .from('beta_user_config')
        .update(updates)
        .eq('user_id', user.id)
        .select()
        .single();

      if (error) throw error;
      setConfig(data as BetaUserConfig);
    } catch (err: any) {
      console.error('Error updating tier:', err);
      setError(err.message);
    }
  };

  useEffect(() => {
    if (user) {
      fetchConfig();
    }
  }, [user]);

  return {
    config,
    todayUsage,
    loading,
    error,
    fetchConfig,
    checkCanPerformOperation,
    recordUsage,
    updateTier,
    canPerformOperation: !loading && !!config && (
      config.ai_credits_remaining > 0 && 
      (todayUsage?.operations_count || 0) < config.daily_limit &&
      config.monthly_spend_cents < config.monthly_budget_cents
    )
  };
};